import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiTool,
  FiFilter,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../../Api';
import { usePermission } from '../../../hooks/usePermission';
import {
  PageComponent,
  Card,
  Table,
  Button,
  Input,
  Select,
  Modal,
  Skeletons,
  TableActions,
  MultiSelect,
} from '../../../components';
import ServiceModal from './ServiceModal';
import '../../CompanyManager/style.css';
import './style.css';

export default function ServiceManager() {
  const permissions = usePermission('service_orders');
  const [selectedCompany] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('selectedCompany'));
    } catch (e) {
      return null;
    }
  });

  const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);



  const loadServices = useCallback(async () => {
    if (!selectedCompany?.id) return;

    if (companies.length === 0) return;

    setLoading(true);
    try {
      const allValue = 'all';
      const specificIds = companyFilterValues.filter(v => v !== allValue);
      const hasAllSelected = companyFilterValues.includes(allValue);

      const companyIds = hasAllSelected || specificIds.length === 0
        ? companies.map(c => c.id)
        : specificIds.map(id => Number(id));

      if (companyIds.length === 0) {
        setServices([]);
        setHasLoadedOnce(true);
        setLoading(false);
        return;
      }

      const allServicesMap = new Map();
      await Promise.all(companyIds.map(async (cId) => {
        try {
          const data = await Api.getServices(cId);
          const loaded = Array.isArray(data) ? data : [];
          loaded.forEach(s => { 
              if (!allServicesMap.has(s.id)) {
                  allServicesMap.set(s.id, s);
              }
          });
        } catch (err) {
          console.error(`Error loading services for company ${cId}`, err);
        }
      }));

      setServices(Array.from(allServicesMap.values()));
      setHasLoadedOnce(true);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar serviços.');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, companies, companyFilterValues]);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilterValues]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);
  }

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  }

  function handleCreate() {
    setSelectedService(null);
    setIsModalOpen(true);
  }

  function handleEdit(service) {
    setSelectedService(service);
    setIsModalOpen(true);
  }

  function handleDelete(service) {
    setServiceToDelete(service);
    setIsDeleteModalOpen(true);
  }

  async function confirmDelete() {
    if (!serviceToDelete) return;
    const cId = serviceToDelete._companyId || serviceToDelete.company_id || selectedCompany?.id;
    if (!cId) return;

    setIsDeleting(true);
    try {
      await Api.deleteService(cId, serviceToDelete.id);
      setServices(prev => prev.filter(s => s.id !== serviceToDelete.id));
      toast.success('Serviço excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setServiceToDelete(null);
    } catch (error) {
      toast.error('Erro ao excluir serviço.');
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleFormSuccess(data) {
    try {
      if (selectedService && selectedService.id) {
        const cId = selectedCompany?.id || 
                    (selectedService.companies && selectedService.companies[0]?.id) || 
                    selectedService.company_id || 
                    (data._companyIds && data._companyIds[0]);

        if (!cId) {
            toast.error('Não foi possível identificar o contexto da empresa.');
            return;
        }

        const updated = await Api.updateService(cId, selectedService.id, data);
        setServices(prev => prev.map(s => s.id === selectedService.id ? updated : s));
        toast.success('Serviço atualizado!');
      } else {
        const { _companyIds } = data;
        const cId = _companyIds && _companyIds.length > 0 ? _companyIds[0] : selectedCompany?.id;

        if (!cId) {
             toast.error('Selecione ao menos uma empresa.');
             return;
        }
        
        const created = await Api.createService(cId, data);
        setServices(prev => [...prev, created]);
        toast.success('Serviço criado!');
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar serviço.');
    }
  }

  const filteredServices = useMemo(() => {
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return services.filter(s => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        (s.name || '').toLowerCase().includes(term) ||
        (s.description || '').toLowerCase().includes(term);

      const serviceCompanyId = s._companyId || s.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        (serviceCompanyId && specificIds.includes(String(serviceCompanyId)));

      return matchesSearch && matchesCompany;
    });
  }, [services, searchTerm, companyFilterValues]);

  const totalItems = filteredServices.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedServices = filteredServices.slice(startIndex, startIndex + pageSize);

  const columns = [
    {
      id: 'name',
      header: 'Nome',
      width: '2.5fr',
      cellClassName: 'manager-table-cell-primary',
      render: (item) => (
        <>
          <div className="manager-table-avatar">
            <FiTool size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{item.name}</span>
            <span className="manager-table-subtitle">{item.description || 'Sem descrição'}</span>
          </div>
        </>
      ),
    },
    {
      id: 'company',
      header: 'Empresas',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: (item) => {
        if (item.companies && item.companies.length > 0) {
            return (
                <span className="manager-table-text">
                    {item.companies.map(c => c.trade_name || c.corporate_name || c.name).join(', ')}
                </span>
            );
        }
        
        const cId = item._companyId || item.company_id;
        const company = companies.find(c => c.id === cId);
        return (
          <span className="manager-table-text">
            {company?.trade_name || company?.corporate_name || company?.name || 'N/A'}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className={`manager-tag ${item.active !== false ? 'manager-tag-success' : 'manager-tag-danger'}`}>
          {item.active !== false ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: (item) => (
        <TableActions>
          {permissions.canEdit && (
            <button type="button" onClick={(e) => { e.stopPropagation(); handleEdit(item); }}>
              <FiEdit2 size={16} /> Editar
            </button>
          )}
          {permissions.canDelete && (
            <button type="button" className="action-danger" onClick={(e) => { e.stopPropagation(); handleDelete(item); }}>
              <FiTrash2 size={16} /> Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiTool size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  function ServiceSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <ServiceSkeletonWrapper />}

        {(!loading || hasLoadedOnce) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiTool size={22} />
                </div>
                <div>
                  <h1>Serviços</h1>
                  <p>Cadastre e gerencie os serviços disponíveis.</p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button onClick={handleCreate} className="manager-header-button">
                  <FiPlus size={18} />
                  Novo Serviço
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {services.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredServices.length} de {services.length} serviços
                  </span>
                )}
              </div>
              <div className="manager-filters-grid">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="filter-company"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({
                        value: String(c.id),
                        label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}`,
                      })),
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="service-search"
                      placeholder="Buscar por nome..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {services.length === 0 ? (
              <div className="empty-state">
                <p>Nenhum serviço cadastrado.</p>
              </div>
            ) : (
              <Table
                items={paginatedServices}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => { setPageSize(value); setCurrentPage(1); }}
                getRowKey={item => item.id}
                emptyMessage="Nenhum serviço encontrado com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <ServiceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={selectedService}
        onSuccess={handleFormSuccess}
        companies={companies}
      />

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o serviço{' '}
            {serviceToDelete && (
              <span className="delete-modal-company-name">{serviceToDelete.name}</span>
            )}?
          </p>
          <p>Essa ação não poderá ser desfeita.</p>
        </div>
        <div className="delete-modal-actions">
          <Button type="button" className="cancel-button" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button type="button" onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
